/**
 * Lightweight IndexedDB-based offline photo storage
 * Replaces PGlite for simple photo storage needs
 */

export interface OfflinePhoto {
	id?: number;
	data_url: string;
	caption: string;
	metadata: any;
	wireident: string;
	created_by: string;
	created_at: Date;
	uploaded: boolean;
	uploaded_url?: string;
}

export class OfflinePhotoStorage {
	private db: IDBDatabase | null = null;
	private dbName = 'lvwr-offline-photos';
	private dbVersion = 1;
	private storeName = 'photos';

	async init(): Promise<void> {
		if (this.db) return;

		return new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, this.dbVersion);

			request.onerror = () => reject(request.error);
			request.onsuccess = () => {
				this.db = request.result;
				resolve();
			};

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;

				if (!db.objectStoreNames.contains(this.storeName)) {
					const store = db.createObjectStore(this.storeName, {
						keyPath: 'id',
						autoIncrement: true
					});

					// Create indexes for common queries
					store.createIndex('uploaded', 'uploaded', { unique: false });
					store.createIndex('wireident', 'wireident', { unique: false });
					store.createIndex('created_at', 'created_at', { unique: false });
				}
			};
		});
	}

	async storePhoto(photoData: Omit<OfflinePhoto, 'id' | 'created_at' | 'uploaded'>): Promise<number> {
		await this.init();

		const photo: Omit<OfflinePhoto, 'id'> = {
			...photoData,
			created_at: new Date(),
			uploaded: false
		};

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);
			const request = store.add(photo);

			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve(request.result as number);
		});
	}

	async getUnuploadedPhotos(): Promise<OfflinePhoto[]> {
		await this.init();

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);
			const index = store.index('uploaded');
			const request = index.getAll(false); // Get all where uploaded = false

			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve(request.result);
		});
	}

	async markAsUploaded(id: number, uploadedUrl: string): Promise<void> {
		await this.init();

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);
			const getRequest = store.get(id);

			getRequest.onerror = () => reject(getRequest.error);
			getRequest.onsuccess = () => {
				const photo = getRequest.result;
				if (photo) {
					photo.uploaded = true;
					photo.uploaded_url = uploadedUrl;

					const updateRequest = store.put(photo);
					updateRequest.onerror = () => reject(updateRequest.error);
					updateRequest.onsuccess = () => resolve();
				} else {
					reject(new Error(`Photo with id ${id} not found`));
				}
			};
		});
	}

	async deletePhoto(id: number): Promise<void> {
		await this.init();

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);
			const request = store.delete(id);

			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve();
		});
	}

	async cleanupUploadedPhotos(): Promise<number> {
		await this.init();

		const uploadedPhotos = await new Promise<OfflinePhoto[]>((resolve, reject) => {
			const transaction = this.db!.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);
			const index = store.index('uploaded');
			const request = index.getAll(true); // Get all where uploaded = true

			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve(request.result);
		});

		// Delete uploaded photos older than 7 days
		const weekAgo = new Date();
		weekAgo.setDate(weekAgo.getDate() - 7);

		let deletedCount = 0;

		for (const photo of uploadedPhotos) {
			if (photo.created_at < weekAgo) {
				await this.deletePhoto(photo.id!);
				deletedCount++;
			}
		}

		return deletedCount;
	}

	async getStorageInfo(): Promise<{ photoCount: number; estimatedSize: number }> {
		await this.init();

		return new Promise((resolve, reject) => {
			const transaction = this.db!.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);
			const request = store.getAll();

			request.onerror = () => reject(request.error);
			request.onsuccess = () => {
				const photos = request.result as OfflinePhoto[];
				const photoCount = photos.length;

				// Estimate size (rough calculation)
				const estimatedSize = photos.reduce((total, photo) => {
					// Base64 data URL is roughly the image size + overhead
					return total + (photo.data_url?.length || 0) +
					       JSON.stringify(photo.metadata || {}).length +
					       (photo.caption?.length || 0) + 200; // overhead
				}, 0);

				resolve({ photoCount, estimatedSize });
			};
		});
	}
}

// Image compression utility
export const compressImage = async (
	dataUrl: string,
	maxWidth: number = 1920,
	quality: number = 0.8
): Promise<string> => {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d')!;

			// Calculate new dimensions
			const ratio = Math.min(maxWidth / img.width, maxWidth / img.height, 1);
			canvas.width = img.width * ratio;
			canvas.height = img.height * ratio;

			// Draw and compress
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

			// Convert to JPEG with quality setting
			const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
			resolve(compressedDataUrl);
		};
		img.src = dataUrl;
	});
};

// Singleton instance
export const offlinePhotoStorage = new OfflinePhotoStorage();